import React, { useEffect, useState, Children } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRightIcon, CheckCircleIcon, TrendingUpIcon, UsersIcon, AwardIcon } from 'lucide-react';
export function Hero() {
  const {
    scrollY
  } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const floatingVariants = {
    animate: {
      y: [0, -10, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    }
  };
  const stats = [{
    icon: AwardIcon,
    value: '20+',
    label: 'Años de Experiencia'
  }, {
    icon: UsersIcon,
    value: '500+',
    label: 'Clientes Satisfechos'
  }, {
    icon: TrendingUpIcon,
    value: '$2B+',
    label: 'en Arrendamientos'
  }];
  const benefits = ['Deducción fiscal hasta 100%', 'Sin inversión inicial', 'Plazos flexibles 2-4 años', 'Asesoría personalizada'];
  return <section id="inicio" className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" data-id="element-151">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden" data-id="element-152">
        <motion.div style={{
        y
      }} className="absolute top-20 right-10 w-96 h-96 bg-[#2BA6DE]/10 rounded-full blur-3xl" data-id="element-153" />
        <motion.div style={{
        y: useTransform(scrollY, [0, 500], [0, -100])
      }} className="absolute bottom-20 left-10 w-96 h-96 bg-[#2BA6DE]/5 rounded-full blur-3xl" data-id="element-154" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-20" data-id="element-155">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-156">
          {/* Left Content */}
          <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-8" data-id="element-157">
            <motion.div variants={itemVariants} className="inline-block" data-id="element-158">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#2BA6DE]/10 border border-[#2BA6DE]/20 text-[#2BA6DE] text-sm font-medium" data-id="element-159">
                <CheckCircleIcon size={16} className="mr-2" data-id="element-160" />
                Soluciones de Arrendamiento Inteligente
              </span>
            </motion.div>

            <motion.h1 variants={itemVariants} className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight" data-id="element-161">
              Estrena activos,{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2BA6DE] to-[#1e8ab8]" data-id="element-162">
                deduce gastos
              </span>
            </motion.h1>

            <motion.p variants={itemVariants} className="text-xl md:text-2xl text-gray-300 leading-relaxed" data-id="element-163">
              Arrendar es la decisión más lógica para hacer crecer tu empresa
              sin comprometer tu capital.
            </motion.p>

            <motion.div variants={itemVariants} className="space-y-3" data-id="element-164">
              {benefits.map((benefit, index) => <div key={index} className="flex items-center space-x-3" data-id="element-165">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#2BA6DE]/20 flex items-center justify-center" data-id="element-166">
                    <CheckCircleIcon size={14} className="text-[#2BA6DE]" data-id="element-167" />
                  </div>
                  <span className="text-gray-300" data-id="element-168">{benefit}</span>
                </div>)}
            </motion.div>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 pt-4" data-id="element-169">
              <button className="group inline-flex items-center justify-center px-8 py-4 bg-[#2BA6DE] text-white rounded-lg font-semibold hover:bg-[#2495c8] transition-all hover:shadow-lg hover:shadow-[#2BA6DE]/25 hover:-translate-y-0.5" data-id="element-170">
                Cotiza tu arrendamiento
                <ArrowRightIcon size={20} className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-171" />
              </button>
              <button className="inline-flex items-center justify-center px-8 py-4 bg-white/5 text-white rounded-lg font-semibold hover:bg-white/10 transition-all border border-white/10" data-id="element-172">
                Ver servicios
              </button>
            </motion.div>
          </motion.div>

          {/* Right Visual Content */}
          <motion.div initial={{
          opacity: 0,
          x: 50
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 1,
          ease: [0.22, 1, 0.36, 1]
        }} className="relative hidden lg:block" data-id="element-173">
            {/* Main Image Card */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl" data-id="element-174">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/caEAEq7hrjsCvqp18eYZn6/hero-img.jpg" alt="Arrendamiento de vehículos" className="w-full h-[500px] object-cover" data-id="element-175" />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/20 to-transparent" data-id="element-176" />
            </div>

            {/* Floating Stat Cards */}
            {stats.map((stat, index) => <motion.div key={index} variants={floatingVariants} animate="animate" style={{
            animationDelay: `${index * 0.2}s`
          }} className="absolute bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-xl" style={{
            top: index === 0 ? '10%' : index === 1 ? '45%' : '80%',
            right: index === 1 ? '-10%' : '5%',
            left: index === 1 ? 'auto' : '-10%'
          }} data-id="element-177">
                <div className="flex items-center space-x-3" data-id="element-178">
                  <div className="w-12 h-12 rounded-lg bg-[#2BA6DE]/10 flex items-center justify-center" data-id="element-179">
                    <stat.icon className="text-[#2BA6DE]" size={24} data-id="element-180" />
                  </div>
                  <div data-id="element-181">
                    <div className="text-2xl font-bold text-gray-900" data-id="element-182">
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-183">{stat.label}</div>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div style={{
      opacity
    }} className="absolute bottom-8 left-1/2 -translate-x-1/2" data-id="element-184">
        <motion.div animate={{
        y: [0, 8, 0]
      }} transition={{
        duration: 1.5,
        repeat: Infinity
      }} className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2" data-id="element-185">
          <motion.div className="w-1.5 h-1.5 bg-white/60 rounded-full" data-id="element-186" />
        </motion.div>
      </motion.div>
    </section>;
}